import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

import { errorLogger } from '@utils/axios/ErrorLogging';

// Aligns with `React.lazy` typing (`ComponentType<any>`) so imported components keep their prop types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- required for lazy import assignability (same as react `lazy()`)
export const lazyLoadWithRetry = <T extends ComponentType<any>>(importer: () => Promise<{ default: T }>, key: string): LazyExoticComponent<T> => {
  const retryImport = async () => {
    try {
      return await importer();
    } catch (error) {
      await new Promise(resolve => {
        const time = setTimeout(() => {
          resolve();
          clearTimeout(time);
        }, 1000);
      });
      try {
        const cache = await caches.open('invalidate_cache');
        await cache.put(key, new Response('true'));
        const response = await importer();
        const path = `${window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'))  }/${  key}`;
        cache.match(path).then(() => {
          cache.delete(path);
        });
        return response;
      } catch (retryError) {
        const isLazyComponentRetry = window.sessionStorage.getItem(`lazyRetryKey-${key}`);
        if (isLazyComponentRetry === null) {
          window.sessionStorage.setItem(`lazyRetryKey-${key}`, 'true');
          if (window.sessionStorage.getItem(`lazyRetryKey-${key}`) === 'true') {
            const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : JSON.stringify(error));
            await errorLogger(err, false, `Lazy component retry-${key}`);
            const cache = await caches.open('invalidate_cache');
            const path = `${window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'))  }/${  key}`;
            cache.match(path).then(() => {
              cache.delete(path);
            });
            window.sessionStorage.removeItem(`lazyRetryKey-${key}`);
            setTimeout(() => window.location.reload(), 100);
          }
        } else {
          const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : JSON.stringify(error));
          await errorLogger(err, false, `Second not reloaded-${key}`);
        }
      }
      throw error;
    }
  };
  return lazy(retryImport);
};
